/**
 * A season's schedule as somebody pastes it (D-371).
 *
 * A team gets its home schedule from the league as a spreadsheet, and the
 * quickest way to a season is to paste those rows: one game per line,
 *
 *   date, time, opponent, promo title, promo detail, promo sponsor, categories, featured
 *
 * comma- or tab-separated (a copy out of a spreadsheet is tab-separated), with
 * an optional header row. Categories are separated by semicolons, because the
 * comma is taken.
 *
 * One reader, used by Admin's preview and by the API when it is handed the raw
 * text, so what the preview says a line means is what the build does with it.
 * The dates and times people type vary ("5/14/2027", "7:05 PM"); both are
 * normalized here to `YYYY-MM-DD` and 24-hour `HH:MM`, and anything that cannot
 * be read is reported against its line rather than guessed at.
 */

export interface ScheduleRow {
  /** 1-based line in the pasted text, for pointing at the mistake. */
  line: number;
  date: string;
  time: string;
  opponent: string;
  promo_title: string | null;
  promo_detail: string | null;
  promo_sponsor: string | null;
  categories: string[];
  featured: boolean;
}

export interface ScheduleRowProblem {
  line: number;
  field: 'date' | 'time' | 'opponent' | 'featured';
  code: 'BAD_DATE' | 'BAD_TIME' | 'OPPONENT_REQUIRED' | 'BAD_FEATURED';
  message: string;
}

export interface ParsedSchedule {
  rows: ScheduleRow[];
  problems: ScheduleRowProblem[];
}

const YES = new Set(['y', 'yes', 'true', '1', 'x', 'featured']);
const NO = new Set(['', 'n', 'no', 'false', '0']);

/** `YYYY-MM-DD`, `M/D/YYYY` or `M/D/YY` → `YYYY-MM-DD`, or null when it is not a real day. */
export function normalizeScheduleDate(raw: string): string | null {
  const value = raw.trim();
  let year: number;
  let month: number;
  let day: number;
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value);
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(value);
  if (iso) {
    year = Number(iso[1]);
    month = Number(iso[2]);
    day = Number(iso[3]);
  } else if (us) {
    month = Number(us[1]);
    day = Number(us[2]);
    year = us[3]!.length === 2 ? 2000 + Number(us[3]) : Number(us[3]);
  } else {
    return null;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** `19:05`, `7:05 PM`, `7:05pm`, `7 PM`, `12:10 p.m.` → 24-hour `HH:MM`, or null. */
export function normalizeScheduleTime(raw: string): string | null {
  const value = raw.trim().toLowerCase().replaceAll('.', '');
  const match = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/.exec(value);
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = match[2] === undefined ? 0 : Number(match[2]);
  const meridiem = match[3];
  if (match[2] === undefined && !meridiem) return null;
  if (minutes > 59) return null;
  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    if (meridiem === 'am' && hours === 12) hours = 0;
    if (meridiem === 'pm' && hours !== 12) hours += 12;
  } else if (hours > 23) {
    return null;
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** The day of the week of a business date: 0 is Sunday, as `Date.getUTCDay` has it. */
export function weekdayOf(businessDate: string): number {
  return new Date(`${businessDate}T12:00:00.000Z`).getUTCDay();
}

/** Splits one line into cells: tab-separated as is, comma-separated with CSV quoting. */
function cells(line: string, delimiter: '\t' | ','): string[] {
  if (delimiter === '\t') return line.split('\t');
  const out: string[] = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]!;
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        current += ch;
      }
    } else if (ch === '"' && current.trim() === '') {
      quoted = true;
      current = '';
    } else if (ch === ',') {
      out.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  out.push(current);
  return out;
}

const text = (value: string | undefined): string | null => {
  const trimmed = (value ?? '').trim();
  return trimmed ? trimmed : null;
};

/**
 * Reads pasted schedule text. Blank lines are skipped, and so is a first line
 * whose first cell says "date" (a header). Every other line becomes a row, even
 * one with problems, so the preview can show it beside what is wrong with it.
 */
export function parseScheduleText(input: string): ParsedSchedule {
  const lines = input.replace(/\r\n?/g, '\n').split('\n');
  const delimiter: '\t' | ',' = lines.some((line) => line.includes('\t')) ? '\t' : ',';
  const rows: ScheduleRow[] = [];
  const problems: ScheduleRowProblem[] = [];
  let seenContent = false;
  for (const [index, line] of lines.entries()) {
    const number = index + 1;
    if (!line.trim()) continue;
    const parts = cells(line, delimiter);
    if (!seenContent && /^\s*date\s*$/i.test(parts[0] ?? '')) {
      seenContent = true;
      continue;
    }
    seenContent = true;
    const rawDate = (parts[0] ?? '').trim();
    const rawTime = (parts[1] ?? '').trim();
    const opponent = (parts[2] ?? '').trim();
    const rawFeatured = (parts[7] ?? '').trim().toLowerCase();
    const date = normalizeScheduleDate(rawDate);
    const time = normalizeScheduleTime(rawTime);
    if (!date) problems.push({ line: number, field: 'date', code: 'BAD_DATE', message: `"${rawDate}" is not a date (use 2027-05-14 or 5/14/2027)` });
    if (!time) problems.push({ line: number, field: 'time', code: 'BAD_TIME', message: `"${rawTime}" is not a time (use 19:05 or 7:05 PM)` });
    if (!opponent) problems.push({ line: number, field: 'opponent', code: 'OPPONENT_REQUIRED', message: 'No opponent' });
    if (!YES.has(rawFeatured) && !NO.has(rawFeatured)) problems.push({ line: number, field: 'featured', code: 'BAD_FEATURED', message: `"${parts[7]?.trim()}" is not yes or no` });
    rows.push({
      line: number,
      date: date ?? rawDate,
      time: time ?? rawTime,
      opponent,
      promo_title: text(parts[3]),
      promo_detail: text(parts[4]),
      promo_sponsor: text(parts[5]),
      categories: (parts[6] ?? '')
        .split(';')
        .map((name) => name.trim())
        .filter(Boolean),
      featured: YES.has(rawFeatured),
    });
  }
  return { rows, problems };
}

/**
 * A game's title from a pattern: `{opponent}` is the team's name, `{short}` its
 * short name (or the name), `{abbr}` its abbreviation (or the short name).
 */
export function gameTitle(pattern: string, opponent: { name: string; short_name: string | null; abbreviation: string | null }): string {
  const short = opponent.short_name?.trim() || opponent.name;
  return pattern
    .replaceAll('{opponent}', opponent.name)
    .replaceAll('{short}', short)
    .replaceAll('{abbr}', opponent.abbreviation?.trim() || short)
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The pattern a template game's title implies. "Bees vs. Quincy Doggy
 * Paddlers" against the Doggy Paddlers is "Bees vs. {opponent}"; a title that
 * names no opponent but reads "… vs. …" keeps what comes before "vs."; anything
 * else is "vs. {opponent}".
 */
export function titlePatternFrom(title: string, opponent: { name: string; short_name: string | null; abbreviation: string | null } | null): string {
  if (opponent) {
    if (title.includes(opponent.name)) return title.replace(opponent.name, '{opponent}');
    if (opponent.short_name && title.includes(opponent.short_name)) return title.replace(opponent.short_name, '{short}');
  }
  const vs = /^(.*?\bvs?\.?\s)/i.exec(title);
  if (vs) return `${vs[1]}{opponent}`;
  return 'vs. {opponent}';
}
